import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeroSection from "./components/HeroSection";
import ConnectionCard from "./components/ConnectionCard";
import SetupProgress from "./components/SetupProgress";
import PrivacyNotice from "./components/PrivacyNotice";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useGlobalAuth } from "../../contexts/GlobalContext";
import PlaylistSyncOverview from "../../pages/playlist-selection-sync-setup/PlaylistSyncOverview";
import ActivityFeedItem from "../dashboard-sync-overview/components/ActivityFeedItem";
// import SyncedPlaylistCard from "../../pages/dashboard-sync-overview/components/SyncedPlaylistCard";
import { getHistoryFromSupabase } from "../../api/SupabaseApi";
import { useSupabaseUser } from "../../contexts/SupabaseUserContext";

const AuthenticationAccountConnection = () => {
  const [activities, setActivities] = useState([]);

  const connectionsRef = useRef(null);
  const { spotify, youtube } = useGlobalAuth();
  const {
    spotifyUserInfo,
    spotifyLogin,
    spotifyLogout,
    playlistCount,
    fetchDataSpotify,
  } = spotify;
  const {
    googleToken,
    googleUserInfo,
    youtubePlaylists,
    youtubeLogin,
    youtubeLogout,
    fetchUserInfoYoutube,
  } = youtube;
  const navigate = useNavigate();
  const supabaseUser = useSupabaseUser();

  const [connections, setConnections] = useState({
    spotify: {
      connected: false,
      username: "",
      playlistCount: 0,
      lastSync: null,
      status: "disconnected",
    },
    youtube: {
      connected: false,
      username: "",
      playlistCount: 0,
      lastSync: null,
      status: "disconnected",
    },
  });
  useEffect(() => {
    if (spotifyUserInfo?.id) {
      setConnections((prev) => ({
        ...prev,
        spotify: {
          connected: true,
          username: spotifyUserInfo?.display_name,
          email: spotifyUserInfo?.email,
          playlistCount: playlistCount,
          lastSync: null,
          status: "active",
        },
      }));
    } else {
      setConnections((prev) => ({
        ...prev,
        spotify: {
          connected: false,
          username: null,
          email: null,
          playlistCount: 0,
          lastSync: null,
          status: "disconnected",
        },
      }));
    }
  }, [spotifyUserInfo, playlistCount]);

  useEffect(() => {
    if (googleUserInfo?.sub) {
      setConnections((prev) => ({
        ...prev,
        youtube: {
          connected: true,
          username: googleUserInfo.name,
          picture: googleUserInfo.picture,
          playlistCount: youtubePlaylists?.length,
          lastSync: null,
          status: "active",
        },
      }));
    } else {
      setConnections((prev) => ({
        ...prev,
        youtube: {
          connected: false,
          username: null,
          picture: null,
          playlistCount: 0,
          lastSync: null,
          status: "disconnected",
        },
      }));
    }
  }, [googleUserInfo, googleToken, youtubePlaylists]);

  const handleConnect = (platform) => {
    if (platform === "spotify") {
      spotifyLogin();
    } else if (platform === "youtube") {
      youtubeLogin();
    }
  };

  const handleDisconnect = (platform) => {
    if (platform === "spotify") {
      spotifyLogout();
    }
    if (platform === "youtube") {
      youtubeLogout();
    }
  };

  const scrollToConnections = () => {
    connectionsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleRefresh = (platform) => {
    if (platform === "spotify") {
      fetchDataSpotify();
    }
    if (platform === "youtube") {
      fetchUserInfoYoutube();
    }
  };

  const connectedCount = Object.values(connections).filter(
    (conn) => conn.connected
  ).length;
  const allConnected = connectedCount === Object.keys(connections).length;

  const handleViewDetails = (activityId) => {
    navigate("/sync-results-management", { state: { activityId } });
  };

  useEffect(() => {
    const fetchActivities = async () => {
      if (!supabaseUser?.id) return;
      const data = await getHistoryFromSupabase(supabaseUser.id);
      // Map Supabase data to ActivityFeedItem format
      const mapped = (data || []).map((item) => ({
        id: item.id,
        playlistName: item.playlist_name,
        direction:
          item.source_platform === "spotify" &&
          item.destination_platform === "youtube"
            ? "spotify_to_youtube"
            : "youtube_to_spotify",
        status: "completed", // You can enhance this if you store status in Supabase
        songsTransferred: item.playlist_songs?.length || 0,
        totalSongs: item.playlist_songs?.length || 0,
        timestamp: item.created_at ? new Date(item.created_at) : new Date(),
        duration: item.playlist_duration
          ? `${Math.floor(item.playlist_duration / 60)}m ${
              item.playlist_duration % 60
            }s`
          : null,
        playlistSongs: item.playlist_songs,
        sourcePlatform: item.source_platform,
        destinationPlatform: item.destination_platform,
      }));
      setActivities(mapped);
    };
    fetchActivities();
  }, [supabaseUser]);

  useEffect(() => {
    // Wait for the ad <ins> element to be in the DOM before pushing
    const interval = setInterval(() => {
      if (window.adsbygoogle && document.querySelector(".adsbygoogle")) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
          clearInterval(interval); // Done
        } catch (e) {
          console.error("AdSense Error:", e);
        }
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 lg:pt-20">
        <div>
          <ins
            className="adsbygoogle"
            style={{ display: "block" }}
            data-ad-client="ca-pub-6539527697946100"
            data-ad-slot="1422093926"
            data-ad-format="auto"
            data-full-width-responsive="true"
          ></ins>
        </div>

        <div className="container mx-auto px-4 lg:px-6 py-8 lg:py-12">
          {/* Hero Section */}
          <HeroSection onScrollToConnections={scrollToConnections} />

          {/* Success Message */}
          {/* {showSuccessMessage && (
            <div className="mb-8 p-4 bg-success/10 border border-success/20 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <div>
                  <p className="text-sm font-medium text-success">
                    Account Connected Successfully!
                  </p>
                  <p className="text-xs text-text-secondary">
                    You can now sync playlists between your connected platforms.
                  </p>
                </div>
              </div>
            </div>
          )} */}

          {/* Connection Cards Section */}
          <div ref={connectionsRef} className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-text-primary mb-2">
                Connect Your Accounts
              </h2>
              <p className="text-text-secondary">
                Link your music streaming accounts to enable cross-platform
                playlist synchronization
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto mb-8">
              <ConnectionCard
                platform="spotify"
                isConnected={connections.spotify.connected}
                userInfo={
                  connections.spotify.connected ? connections.spotify : null
                }
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                handleRefresh={handleRefresh}
              />

              <ConnectionCard
                platform="youtube"
                isConnected={connections.youtube.connected}
                userInfo={
                  connections.youtube.connected ? connections.youtube : null
                }
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                handleRefresh={handleRefresh}
              />
            </div>

            {allConnected ? (
              <PlaylistSyncOverview />
            ) : (
              <div className="max-w-2xl mx-auto mb-8">
                <SetupProgress connections={connections} />
              </div>
            )}

            {/* {allConnected && (
              <div className="text-center">
                <div className="p-6 bg-success/10 border border-success/20 rounded-xl max-w-md mx-auto">
                  <Icon name="CheckCircle" size={32} className="text-success mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-text-primary mb-2">Setup Complete!</h3>
                  <p className="text-sm text-text-secondary mb-4">
                    All accounts are connected. You're ready to start syncing playlists.
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleProceedToDashboard}
                    iconName="ArrowRight"
                    iconSize={20}
                    className="w-full"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            )} */}
          </div>

          {/* Privacy Notice */}
          <div className="max-w-6xl mx-auto">
            <PrivacyNotice />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 lg:gap-8 max-w-6xl mx-auto my-6">
            {/* Recent Activity */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary">
                  Recent Activity
                </h2>
                {/* <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/sync-results-management")}
                  iconName="ExternalLink"
                  iconSize={14}
                >
                  View All
                </Button> */}
              </div>

              <div className="space-y-3">
                {activities.slice(0, 4).map((activity) => (
                  <ActivityFeedItem
                    key={activity.id}
                    activity={activity}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            </section>

            {/* Synced Playlists */}
            {/* <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary">
                  My Synced Playlists
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/sync-results-management")}
                  iconName="Settings"
                  iconSize={14}
                >
                  Manage
                </Button>
              </div>

              <div className="space-y-3">
                {syncedPlaylists.map((playlist) => (
                  <SyncedPlaylistCard
                    key={playlist.id}
                    playlist={playlist}
                    onManage={handleManagePlaylist}
                    onResync={handleResyncPlaylist}
                  />
                ))}
              </div>
            </section> */}
          </div>

          {/* Help Section */}
          <div className="mt-12 text-center">
            <div className="p-6 bg-surface border border-border rounded-xl max-w-2xl mx-auto">
              <Icon
                name="HelpCircle"
                size={24}
                className="text-primary mx-auto mb-4"
              />
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Need Help?
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Having trouble connecting your accounts? Check our
                troubleshooting guide or contact support.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Book"
                  iconSize={16}
                  onClick={() => navigate("/view-guide")}
                >
                  View Guide
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="MessageCircle"
                  iconSize={16}
                  onClick={() => navigate("/contact-support")}
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuthenticationAccountConnection;
